import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap'
import PageHeading from '../../views/PageHeading'
import { ContainsAuthExpiredError, ErrorDetail } from "../../models/ErrorDetail"
import { Link, Redirect } from "react-router-dom"
import MyFfbfClientApi from '../../api/MyFfbfClientApi'
import OverlaySpinner from '../../views/OverlaySpinner'
import { ApiBaseUrl } from '../../api/ApiUtilities'
import cares from '../../images/cares.png'
import { ExternalLink } from '../../views/ExternalLink'

const StrengthenYourMembership = () => {
    const { isLoading, errors, payload } = MyFfbfClientApi.Account.UseAccountOverview() 
	const accountOverview = payload

    if (ContainsAuthExpiredError(errors)) {
        return <Redirect to="/login" />
    }
    if (accountOverview && accountOverview.MembershipSummary.IsTerminated) {
        return <Redirect to="/myMembership/PayDues" />
    }

    const getUrl = (keyword: string) => `${ApiBaseUrl}Account/GetDownloadableFile/${keyword}`;

    return <>
        { isLoading && <OverlaySpinner /> }
        <PageHeading>
            Strengthen <b>Your Membership</b>
        </PageHeading>
        <Container className="pageContent">
            <Row>
                <Col className="underlinedLinks">

                    <p>
                        Florida Farm Bureau Federation develops our members into better leaders, advocators and policy developers. 
                        Members have opportunities to grow, network, learn, lead, advocate through leadership programs.
                    </p>

                    <hr />

                    <h2>Events</h2>

                    <p>
                        Stay in touch and involved with Florida Farm Bureau statewide events!
                    </p>

                    <p>
                        <b>Early Bird Registration Now Open!</b><br />
                        Join Florida farmers, ranchers and producers at the 85th State Annual Meeting in Daytona Beach, FL, Oct. 28-30, 2026!<br />
                        <a href="https://floridafarmbureau.formstack.com/forms/2026_annual_meeting" target="_blank">Click here to Register!</a>
                    </p>

                    <hr />

                    <h3>County Farm Bureau </h3>

                    <p>
                        We are a grassroots organization, so all development starts at
                        the <a href="https://floridafarmbureau.org/county-farm-bureaus/" target="_blank">County Farm Bureau</a>.
                        Membership dues go back to your county farm bureau for them to continue advocating for agriculture,
                        education and philanthropic activities. All members have the opportunity to get involved by volunteering
                        or participating in a leadership committee. Active (Farming and Ranching members) may participate on
                        their county board to develop policy.
                    </p>

                    <p>
                        To participate locally, <Link to='/contactUs'>contact your County Farm Bureau</Link>.
                    </p>

                    <hr />

                    <h3>Young Farmers and Ranchers (YF&amp;R)</h3>

                    <p>
                        Young Farmers &amp; Ranchers are between the ages of 18-35 who have a desire to network, share ideas,
                        participate in community projects and hone their leadership skills. YF&amp;R committees are available
                        across the state in your County Farm Bureau. At the state level, the YF&amp;R Leadership Program provides
                        many opportunities for young agriculturists to be involved at the community, local, state and national level.
                        Various events and award programs recognize the importance of young farmers' and ranchers' contributions
                        to agriculture.
                    </p>

                    <p>
                        To participate locally with YF&amp;R, <Link to='/contactUs'>contact your County Farm Bureau</Link>. 
                        To participate in the state
                        leadership group, applications open every two years. Information on the FFBF Young Farmers &amp; Ranchers
                        Leadership Program can be found <a href="https://floridafarmbureau.org/yfrleadershipgroup/" target="_blank">here</a>.
                    </p>

                    <ul>
                        <li>
                            <strong>Young Farmers and Ranchers Leadership Development Guide</strong>
                            <ul>
                                <li>
                                    <p>Whether you are starting a new county Young Farmers and Ranchers Leadership program or you have
                                served as the chair for years, the Young Farmers and Ranchers Leadership Development Manual serves
                                as a guide for making your county program thrive and continue to strengthen your community!</p>
                                    <p>
                                        <ExternalLink href={getUrl("YFR")} linkClass="btn btn-primary">YFR Leadership Guide</ExternalLink>
                                    </p>
                                </li>
                            </ul>
                        
                        </li>
                    </ul>

                    <hr />

                    <h3>Women's Leadership </h3>

                    <p>
                        Women’s Leadership Program uses the unique talents of women to help support Farm Bureau in organizational, legislative, educational and promotional initiatives. Any woman from a Farm Bureau family is encouraged to participate in the Farm Bureau Women’s Leadership Program. Women are key in sharing agriculture’s story at the local, state and national level.
                    
                        To participate in your County Farm Bureau Women’s Program, <Link to='/contactUs'>contact your County Farm Bureau</Link>.
                        To participate as a district chair on the state Women’s Leadership Committee, contact us 
                        at <a href="mailto:Women.Leadership@ffbf.org">Women.Leadership@ffbf.org</a>.
                    </p>

                    <p>
                        <a className="btn btn-primary" target="_blank" href="https://floridafarmbureau.org/programs/farm-bureau-women/">Learn More</a>
                    </p>

                    <hr />

                    <h3>Florida Farm Bureau Policy </h3>

                    <p>
                        Have you ever wondered what shapes the policy that guides the action of FFBF? Members are the sole drivers
                        of our organization's beliefs, philosophies, and ideas that have been adopted over the years. The policy
                        book represents members' collective viewpoints and directs how we advocate for those who provide our Food,
                        Fuel and Fiber.
                    </p>

                    <p>
                        <ExternalLink href={getUrl("PolicyBook")} linkClass="btn btn-primary">FFBF Policy Book</ExternalLink>
                    </p>

                    <ul>
                        <li>
                            <strong>Advisory Committees </strong>
                            <p>
                                Florida Farm Bureau's 16 advisory committees are engaged in policy development and guide our state
                                board of directors on pertinent issues. Committee members are volunteer leaders actively involved
                                in farming and the commodity they represent.
                            </p>

                            <p>
                                If you are interested in serving on an advisory committee, contact 
                                your <a href="https://floridafarmbureau.org/county-farm-bureaus/" target="_blank">County Farm Bureau</a> President.
                            </p>

                            <p>
                                <ExternalLink href={getUrl("AdvisHandbook")} linkClass="btn btn-primary">State Advisory Committee Member Handbook</ExternalLink>
                            </p>

                        </li>
                    </ul>

                    <hr />

                    <img src={cares} alt="" style={{float: "right", width: "20%"}} />

                    <h3>CARES </h3>

                    <p>
                        For more than 25 years, Florida Farm Bureau's County Alliance for Responsible Environmental Stewardship
                        (CARES) program has publicly recognized farmers and ranchers who have demonstrated exemplary efforts to
                        protect Florida's natural resources.
                    </p>

                    <p>
                        To date, more than 900 farmers and ranchers have received a CARES designation. Recipients are presented
                        with a customized This Farm CARES sign to display at their farm gate or on a nearby roadway to help area
                        residents understand farmers and ranchers are committed to protecting Florida's natural resources. Through
                        in-person events, farm tours and digital media, the CARES program also promotes environmental stewardship
                        statewide. Visit <a href="https://thisfarmcares.org" target="_blank">ThisFarmCARES.org</a> to
                        learn about CARES recipients from your county!
                    </p>

                </Col>
            </Row>
        </Container>
    </>
}

export default StrengthenYourMembership